import React, { Component } from "react";
import type { ReactNode } from "react";
import { Button, Container, Stack, Text, Paper } from "@mantine/core";
import { IconAlertCircle, IconRefresh } from "@tabler/icons-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ClientListErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to monitoring service
    console.error("ClientListErrorBoundary caught an error:", error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        this.props.fallback || (
          <Container py="xl">
            <Paper
              p="xl"
              radius="md"
              withBorder
              style={{
                borderColor: "#fee2e2",
                backgroundColor: "#fef2f2",
              }}
            >
              <Stack align="center" gap="md">
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    backgroundColor: "#fecaca",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconAlertCircle size={32} color="#dc2626" />
                </div>
                <Stack align="center" gap="xs">
                  <Text size="xl" fw={600} c="#991b1b">
                    Something went wrong
                  </Text>
                  <Text size="sm" c="#7f1d1d" ta="center" maw={400}>
                    There was an error loading the client list. Please try
                    refreshing the page or contact support if the problem
                    persists.
                  </Text>
                </Stack>
                <Button
                  leftSection={<IconRefresh size={18} />}
                  onClick={this.handleRetry}
                  variant="filled"
                  color="red"
                  size="md"
                  mt="sm"
                  radius="md"
                >
                  Try Again
                </Button>
              </Stack>
            </Paper>
          </Container>
        )
      );
    }

    return this.props.children;
  }
}
